import { useEffect, type ReactNode } from 'react'
import Column from '@app/components/layout/Column'
import { match } from 'ts-pattern'
import { useQuery } from '@based/sdk/react'
import { RegionViewModel } from 'functions/app/geography/view/region'
import { regionTabs } from '@app/pages/discover/regions/constants'
import { Initiatives } from '../regionTabs/tabContent/Initiatives'
import { Thread } from '@app/components/blocks/thread/Thread'
import { KnowledgeHubModulesSection } from '@app/components/blocks/KnowledgeHubModulesSection'
import { RegionalServiceProvidersGrid } from '../regionTabs/tabContent/RegionalServiceProvidersGrid'
import { ServiceProviderListRegionalModel } from 'functions/app/service-provider/list-regional'
import { RegionalActueelTab } from './RegionalActueelTab'
import { RegionalMunicipalitiesGrid } from './RegionalMunicipalitiesGrid'
import { useRegionalLayoutTabAvailability } from './useRegionalLayoutTabAvailability'
import type { RegionFeatures } from '@schema/featureToggle'
import { isFeatureEnabled } from '@app/util/featureToggle'
import { navigate } from 'wouter/use-browser-location'
import { pages } from '@app/util/links'

export const RegionalPageTabContent = ({
  region,
  regionSlug,
  tab,
}: {
  region: RegionViewModel
  regionSlug: string
  tab: RegionFeatures
}) => {
  const { isTabAvailable } = useRegionalLayoutTabAvailability(region)

  useEffect(() => {
    if (tab === regionTabs.overview) {
      return
    }
    if (!isTabAvailable(tab)) {
      navigate(pages.regionPageNew(regionSlug))
    }
  }, [tab, isTabAvailable, regionSlug])

  const expertsFeatureEnabled = isFeatureEnabled(
    region.featureToggles,
    regionTabs.experts,
  )
  const regionalExpertsListQuery = useQuery(
    tab === regionTabs.experts && expertsFeatureEnabled
      ? 'list-regional-service-providers'
      : null,
    { regionId: region.id },
  )
  const regionalExperts: ServiceProviderListRegionalModel[] =
    regionalExpertsListQuery.data !== undefined
      ? (regionalExpertsListQuery.data as ServiceProviderListRegionalModel[])
      : []

  if (tab === regionTabs.overview) {
    return null
  }

  return (
    <Column alignItems="stretch" gap="2rem">
      {match(tab)
        .with(regionTabs.current, () => (
          <RegionalActueelTab
            key="current"
            region={region}
            regionSlug={regionSlug}
          />
        ))
        .with(regionTabs.discussion, () => (
          <Thread
            key="thread"
            threadId={region.thread?.id}
            columns={3}
          />
        ))
        .with(regionTabs.initiatives, () => (
          <Initiatives key="initiatives" entity={region} />
        ))
        .with(regionTabs.municipalities, () => (
          <RegionalMunicipalitiesGrid
            key="municipalities"
            regionId={region.id}
          />
        ))
        .with(regionTabs.knowledgeHub, () => (
          <KnowledgeHubModulesSection
            key="knowledgeHub"
            modules={region.knowledgeHubModules ?? []}
            getModuleLink={(slug) =>
              pages.regionKnowledgeHubModule(regionSlug, slug)
            }
          />
        ))
        .with(regionTabs.experts, () => (
          <RegionalServiceProvidersGrid
            key="experts"
            serviceProviders={regionalExperts}
          />
        ))
        .with(regionTabs.subsidies, (): ReactNode => null)
        .exhaustive()}
    </Column>
  )
}
